import { debug } from '../utils/Debug.js';

/**
 * Reusable debug overlay that can be toggled with SHIFT+D.
 * 
 * Usage:
 *   const debugMenu = new DebugMenu();
 *   debugMenu.init();
 *   debugMenu.addButton('SPAWN GOLD', () => { console.log('Spawning gold...'); });
 *   debugMenu.setButtons([
 *     { label: 'ACTION 1', onClick: () => {} },
 *     { label: 'ACTION 2', onClick: () => {} }
 *   ]);
 *   debugMenu.dispose(); // cleanup when done
 */
export class DebugMenu {
    constructor() {
        this.overlay = null;
        this.isVisible = false;
        this.buttons = [];
        this.toggles = [];
        this.keydownHandler = null;
    }

    init() {
        this.overlay = document.createElement('div');
        this.overlay.className = 'debug-overlay';
        this.overlay.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: rgba(0, 0, 0, 0.85);
            border: 1px solid var(--color-indigo);
            padding: 15px;
            border-radius: 5px;
            display: none;
            flex-direction: column;
            gap: 10px;
            min-width: 200px;
            z-index: 1000;
        `;

        const title = document.createElement('div');
        title.className = 'debug-title';
        title.textContent = 'DEBUG';
        title.style.cssText = `
            color: var(--color-indigo);
            font-size: 0.9rem;
            letter-spacing: 0.3rem;
            text-align: center;
            margin-bottom: 5px;
            opacity: 0.8;
        `;
        this.overlay.appendChild(title);

        this.addToggle('Debug Logs', debug.enabled, (enabled) => {
            debug.setEnabled(enabled);
        });

        document.body.appendChild(this.overlay);

        this.keydownHandler = (e) => {
            if (e.repeat) return;
            if (e.shiftKey && e.code === 'KeyD') {
                this.toggle();
            }
        };
        document.addEventListener('keydown', this.keydownHandler);
    }

    addButton(label, onClick) {
        const button = document.createElement('button');
        button.className = 'debug-btn';
        button.textContent = label;
        button.style.cssText = `
            font-size: 0.9rem;
            padding: 8px 15px;
            border: 1px solid var(--color-indigo);
            color: var(--color-indigo);
            background: transparent;
            cursor: pointer;
            transition: all 0.2s ease;
            text-transform: uppercase;
            letter-spacing: 0.1rem;
        `;

        button.addEventListener('mouseenter', () => {
            button.style.background = 'rgba(138, 43, 226, 0.2)';
            button.style.boxShadow = '0 0 10px var(--color-indigo)';
        });

        button.addEventListener('mouseleave', () => {
            button.style.background = 'transparent';
            button.style.boxShadow = 'none';
        });

        button.addEventListener('click', onClick);

        this.overlay.appendChild(button);
        this.buttons.push(button);
    }

    addToggle(label, initialValue, onChange) {
        const container = document.createElement('div');
        container.className = 'debug-toggle';
        container.style.cssText = `
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 8px 15px;
            border: 1px solid var(--color-indigo);
            color: var(--color-indigo);
            font-size: 0.9rem;
            text-transform: uppercase;
            letter-spacing: 0.1rem;
        `;

        const labelEl = document.createElement('span');
        labelEl.textContent = label;

        const toggle = document.createElement('button');
        toggle.style.cssText = `
            width: 50px;
            height: 24px;
            border: 1px solid var(--color-indigo);
            border-radius: 12px;
            background: ${initialValue ? 'var(--color-indigo)' : 'transparent'};
            cursor: pointer;
            position: relative;
            transition: all 0.2s ease;
        `;

        const knob = document.createElement('div');
        knob.style.cssText = `
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: ${initialValue ? '#000' : 'var(--color-indigo)'};
            position: absolute;
            top: 2px;
            left: ${initialValue ? '28px' : '2px'};
            transition: all 0.2s ease;
        `;
        toggle.appendChild(knob);

        let isOn = initialValue;
        toggle.addEventListener('click', () => {
            isOn = !isOn;
            toggle.style.background = isOn ? 'var(--color-indigo)' : 'transparent';
            knob.style.background = isOn ? '#000' : 'var(--color-indigo)';
            knob.style.left = isOn ? '28px' : '2px';
            onChange(isOn);
        });

        container.appendChild(labelEl);
        container.appendChild(toggle);
        this.overlay.appendChild(container);
        this.toggles.push(container);
    }

    addInfoSection(title, getContentFn) {
        const section = document.createElement('div');
        section.className = 'debug-info-section';
        section.style.cssText = `
            border: 1px solid var(--color-indigo);
            border-radius: 3px;
            overflow: hidden;
        `;

        const header = document.createElement('div');
        header.style.cssText = `
            padding: 6px 12px;
            color: var(--color-indigo);
            font-size: 0.75rem;
            text-transform: uppercase;
            letter-spacing: 0.15rem;
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            opacity: 0.8;
        `;
        header.textContent = title;

        const arrow = document.createElement('span');
        arrow.textContent = '▸';
        arrow.style.transition = 'transform 0.2s ease';
        header.appendChild(arrow);

        const content = document.createElement('div');
        content.style.cssText = `
            display: none;
            padding: 8px 12px;
            font-family: monospace;
            font-size: 0.75rem;
            color: var(--color-indigo);
            line-height: 1.6;
            border-top: 1px solid rgba(138, 43, 226, 0.3);
        `;

        let isOpen = false;
        header.addEventListener('click', () => {
            isOpen = !isOpen;
            content.style.display = isOpen ? 'block' : 'none';
            arrow.style.transform = isOpen ? 'rotate(90deg)' : '';
            if (isOpen) content.innerHTML = getContentFn();
        });

        section.appendChild(header);
        section.appendChild(content);
        this.overlay.appendChild(section);
        this.buttons.push(section);
    }

    clearButtons() {
        this.buttons.forEach(btn => btn.remove());
        this.buttons = [];
        this.toggles.forEach(toggle => toggle.remove());
        this.toggles = [];
    }

    setButtons(buttonConfigs) {
        this.clearButtons();
        buttonConfigs.forEach(config => {
            this.addButton(config.label, config.onClick);
        });
    }

    toggle() {
        this.isVisible = !this.isVisible;
        this.overlay.style.display = this.isVisible ? 'flex' : 'none';
    }

    show() {
        this.isVisible = true;
        this.overlay.style.display = 'flex';
    }

    hide() {
        this.isVisible = false;
        this.overlay.style.display = 'none';
    }

    dispose() {
        if (this.keydownHandler) {
            document.removeEventListener('keydown', this.keydownHandler);
        }
        this.clearButtons();
        if (this.overlay && this.overlay.parentNode) {
            this.overlay.remove();
        }
        this.overlay = null;
    }
}
